class CustomHeader extends HTMLElement {
  constructor() {
    super();

    this.account = this.querySelector('[data-account]');
    this.accountBtn = this.querySelector('[data-account-toggle]');

    this.drawerWrapper = this.querySelector('[data-drawer-wrapper]');
    this.drawer = this.querySelector('[data-drawer]');
    this.drawerBtn = this.querySelector('[data-drawer-toggle]');
    this.drawerCloseBtn = this.querySelector('[data-drawer-close]');

    this.search = this.querySelector('[data-search]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.searchResults = this.querySelector('[data-search-results]');

    this.searchMobile = this.querySelector('[data-search-mobile]');
    this.searchInputMobile = this.querySelector('[data-search-input-mobile]');
    this.searchResultsMobile = this.querySelector('[data-search-results-mobile]');

    this.priceToggle = this.querySelector('[data-price-toggle]');

    this.navItems = this.querySelectorAll('[data-nav-item]');
    this.megaPanel = this.querySelector('[data-mega-panel]');
    this.middleRow = this.querySelector('.custom-header__row-shell--middle');

    // MMC-023: deep menu levels can be lazy-loaded (data-menu-url), so screens/panels are queried dynamically.
    this.menuUrl = this.dataset.menuUrl || '';
    this.menuRoot = null;
    this.menuPromise = null;
    this.menuInjected = false;
    this.pendingMegaItem = null;

    this.debounceTimerDesktop = null;
    this.debounceTimerMobile = null;
    this.closeMegaTimer = null;

    this.lastScrollY = window.scrollY;
    this.ticking = false;
  }

connectedCallback() {
  this.bindAccount();
  this.bindDrawer();
  this.bindDrawerNavigation();
  this.bindMenuPrefetch();
  this.bindOutsideClick();
  this.bindEscape();
  this.bindPriceToggle();
  this.bindPredictiveSearch();
  this.bindMegaMenu();
  this.bindStickyReveal();
  this.bindStickySpacer();
}

  closeAll(except = null) {
    if (except !== 'account' && this.account) {
      this.account.classList.remove('is-open');
      if (this.accountBtn) this.accountBtn.setAttribute('aria-expanded', 'false');
    }

    if (except !== 'drawer' && this.drawerWrapper) {
      this.drawerWrapper.classList.remove('is-open');
      if (this.drawerBtn) this.drawerBtn.setAttribute('aria-expanded', 'false');
      this.resetMobileMenu();
    }

    if (except !== 'search' && this.search) {
      this.search.classList.remove('is-open');
      if (this.searchResults) this.searchResults.innerHTML = '';
    }

    if (except !== 'search-mobile' && this.searchMobile) {
      this.searchMobile.classList.remove('is-open');
      if (this.searchResultsMobile) this.searchResultsMobile.innerHTML = '';
    }

    if (except !== 'mega' && this.megaPanel) {
      this.closeMega();
    }
  }

  bindAccount() {
    if (!this.account || !this.accountBtn) return;

    this.accountBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const isOpen = this.account.classList.contains('is-open');
      this.closeAll();

      if (!isOpen) {
        this.account.classList.add('is-open');
        this.accountBtn.setAttribute('aria-expanded', 'true');
      }
    });
  }

  bindDrawer() {
    if (!this.drawerWrapper || !this.drawerBtn) return;

    this.drawerBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const isOpen = this.drawerWrapper.classList.contains('is-open');
      this.closeAll();

      if (!isOpen) {
        this.drawerWrapper.classList.add('is-open');
        this.drawerBtn.setAttribute('aria-expanded', 'true');
        this.loadMenu().then(() => this.injectDrawerLevels());
      }
    });

    if (this.drawerCloseBtn) {
      this.drawerCloseBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeAll();
      });
    }

    if (this.drawer) {
      this.drawer.addEventListener('click', (event) => {
        event.stopPropagation();
      });
    }
  }

bindStickySpacer() {
  if (!this.classList.contains('is-sticky')) return;

  const setOffset = () => {
    const headerHeight = this.offsetHeight;
    document.documentElement.style.setProperty('--custom-header-offset', `${headerHeight}px`);
    document.body.style.paddingTop = `${headerHeight}px`;
  };

  setOffset();
  window.addEventListener('resize', setOffset);
  window.addEventListener('load', setOffset);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(setOffset);
  }
}

  /* ---------- MMC-023: lazy deep menu levels (Section Rendering API) ---------- */
  loadMenu() {
    if (this.menuPromise) return this.menuPromise;
    if (!this.menuUrl) {
      this.menuPromise = Promise.resolve(null);
      return this.menuPromise;
    }
    this.menuPromise = fetch(this.menuUrl, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('menu ' + response.status);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const root = doc.querySelector('[data-mmc-menu-fragments]');
        if (!root) throw new Error('menu fragments missing');
        this.menuRoot = root;
        return root;
      })
      .catch((error) => {
        console.warn('[custom-header] menu lazy-load failed, falling back to links', error);
        this.menuFailed = true;
        return null;
      });
    return this.menuPromise;
  }

  bindMenuPrefetch() {
    if (!this.menuUrl) return;
    const prefetch = () => this.loadMenu();
    const middle = this.middleRow;
    if (middle) {
      middle.addEventListener('pointerover', prefetch, { once: true, passive: true });
      middle.addEventListener('focusin', prefetch, { once: true });
    }
    if (this.drawerBtn) {
      ['pointerdown', 'touchstart', 'focus', 'mouseenter'].forEach((type) => {
        this.drawerBtn.addEventListener(type, prefetch, { once: true, passive: true });
      });
    }
  }

  injectDrawerLevels() {
    if (this.menuInjected || !this.menuRoot) return;
    this.querySelectorAll('[data-mmc-slot]').forEach((slot) => {
      const part = this.menuRoot.querySelector(`[data-mmc-part="${slot.dataset.mmcSlot}"]`);
      if (part) slot.innerHTML = part.innerHTML;
    });
    this.menuInjected = true;
    const activeTab = this.querySelector('[data-drawer-tab].is-active');
    if (activeTab) this.activateDrawerTab(activeTab, false);
  }

  getMegaTemplate(item) {
    const index = item.dataset.megaIndex;
    const inline = this.querySelector(`[data-mmc-inline-mega] template[data-mega-index="${index}"]`) || item.querySelector('[data-mega-template]');
    if (inline) return Promise.resolve(inline);
    return this.loadMenu().then((root) => (root ? root.querySelector(`template[data-mega-index="${index}"]`) : null));
  }

  activateDrawerTab(tab, navigateIfMissing = true) {
    const target = tab.dataset.target;
    const targetPanel = target ? this.querySelector(`#${CSS.escape(target)}`) : null;
    if (!targetPanel) {
      if (this.menuUrl && !this.menuFailed && !this.menuInjected) {
        this.loadMenu().then(() => {
          this.injectDrawerLevels();
          if (this.querySelector(`#${CSS.escape(target)}`)) this.activateDrawerTab(tab, navigateIfMissing);
          else if (navigateIfMissing && tab.dataset.href) window.location.href = tab.dataset.href;
        });
        return;
      }
      if (navigateIfMissing && tab.dataset.href) window.location.href = tab.dataset.href;
      return;
    }
    this.querySelectorAll('[data-drawer-tab]').forEach((btn) => btn.classList.remove('is-active'));
    this.querySelectorAll('[data-drawer-panel]').forEach((panel) => panel.classList.remove('is-active'));
    tab.classList.add('is-active');
    targetPanel.classList.add('is-active');
  }

  goToMobileScreen(button, fromKeyboard) {
    const target = button.dataset.mobileNext;
    if (this.querySelector(`[data-mobile-screen="${target}"]`)) {
      this.showMobileScreen(target, fromKeyboard);
      return;
    }
    if (this.menuUrl && !this.menuFailed && !this.menuInjected) {
      button.setAttribute('aria-busy', 'true');
      this.loadMenu().then(() => {
        button.removeAttribute('aria-busy');
        this.injectDrawerLevels();
        if (this.querySelector(`[data-mobile-screen="${target}"]`)) this.showMobileScreen(target, fromKeyboard);
        else if (button.dataset.href) window.location.href = button.dataset.href;
      });
      return;
    }
    if (button.dataset.href) window.location.href = button.dataset.href;
  }

  bindDrawerNavigation() {
    // Delegated on the drawer (the drawer stops click propagation), works for inline and lazy-injected markup.
    if (!this.drawer) return;
    this.drawer.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-drawer-tab]');
      if (tab) {
        this.activateDrawerTab(tab);
        return;
      }
      const next = event.target.closest('[data-mobile-next]');
      if (next) {
        this.goToMobileScreen(next, event.detail === 0);
        return;
      }
      const back = event.target.closest('[data-mobile-back]');
      if (back) this.showMobileScreen(back.dataset.mobileBack, event.detail === 0);
    });
  }

  showMobileScreen(target, focusScreen = false) {
    let shown = null;
    this.querySelectorAll('[data-mobile-screen]').forEach((screen) => {
      const active = screen.dataset.mobileScreen === target;
      screen.classList.toggle('is-active', active);
      if (active) shown = screen;
    });
    if (focusScreen && shown) {
      const focusable = shown.querySelector('button, a[href]');
      if (focusable) focusable.focus();
    }
  }

  resetMobileMenu() {
    if (!this.querySelector('[data-mobile-screen]')) return;
    this.showMobileScreen('level-1');
  }

  bindOutsideClick() {
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) {
        this.closeAll();
      }
    });
  }

  bindEscape() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeAll();
      }
    });
  }

  bindPriceToggle() {
    if (!this.priceToggle) return;

    const buttons = this.priceToggle.querySelectorAll('button');
    const storageKey = 'custom-price-display';
    const saved = window.localStorage.getItem(storageKey);

    if (saved) {
      buttons.forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.value === saved);
      });
      document.documentElement.setAttribute('data-price-display', saved);
    }

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset.value;
        buttons.forEach((btn) => btn.classList.remove('is-active'));
        button.classList.add('is-active');
        window.localStorage.setItem(storageKey, value);
        document.documentElement.setAttribute('data-price-display', value);
      });
    });
  }

  bindPredictiveSearch() {
    if (this.search && this.searchInput && this.searchResults) {
      this.searchInput.addEventListener('input', () => {
        window.clearTimeout(this.debounceTimerDesktop);
        const query = this.searchInput.value.trim();

        if (query.length < 2) {
          this.search.classList.remove('is-open');
          this.searchResults.innerHTML = '';
          return;
        }

        this.closeAll('search');

        this.debounceTimerDesktop = window.setTimeout(() => {
          this.fetchPredictiveResults(query, this.search, this.searchResults);
        }, 250);
      });

      this.search.addEventListener('click', (event) => {
        event.stopPropagation();
      });
    }

    if (this.searchMobile && this.searchInputMobile && this.searchResultsMobile) {
      this.searchInputMobile.addEventListener('input', () => {
        window.clearTimeout(this.debounceTimerMobile);
        const query = this.searchInputMobile.value.trim();

        if (query.length < 2) {
          this.searchMobile.classList.remove('is-open');
          this.searchResultsMobile.innerHTML = '';
          return;
        }

        this.closeAll('search-mobile');

        this.debounceTimerMobile = window.setTimeout(() => {
          this.fetchPredictiveResults(query, this.searchMobile, this.searchResultsMobile);
        }, 250);
      });

      this.searchMobile.addEventListener('click', (event) => {
        event.stopPropagation();
      });
    }
  }

  fetchPredictiveResults(query, searchWrapper, resultsContainer) {
    const url =
      `${window.Shopify.routes.root}search/suggest` +
      `?q=${encodeURIComponent(query)}` +
      `&resources[type]=product,collection,page,article` +
      `&resources[limit]=6` +
      `&section_id=predictive-search`;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const results = html.querySelector('#shopify-section-predictive-search');

        if (results) {
          resultsContainer.innerHTML = results.innerHTML;
        } else {
          resultsContainer.innerHTML = '<p>No results found.</p>';
        }

        searchWrapper.classList.add('is-open');
      })
      .catch(() => {
        resultsContainer.innerHTML = '<p>Something went wrong. Please try again.</p>';
        searchWrapper.classList.add('is-open');
      });
  }

  openMega(item, focusFirst = false) {
    if (window.innerWidth <= 989) return;
    window.clearTimeout(this.closeMegaTimer);

    if (!item.hasAttribute('data-has-mega')) {
      this.closeMega();
      return;
    }
    if (this.megaPanel.dataset.owner === item.dataset.megaIndex && this.megaPanel.classList.contains('is-open')) {
      if (focusFirst) this.focusMegaLink(0);
      return;
    }

    this.pendingMegaItem = item;
    this.getMegaTemplate(item).then((template) => {
      if (this.pendingMegaItem !== item) return;
      if (!template) {
        this.pendingMegaItem = null;
        return;
      }
      this.closeAll('mega');
      this.navItems.forEach((navItem) => {
        navItem.classList.remove('is-active');
        const l = navItem.querySelector('.custom-header__nav-link');
        if (l && l.hasAttribute('aria-expanded')) l.setAttribute('aria-expanded', 'false');
      });
      item.classList.add('is-active');
      const link = item.querySelector('.custom-header__nav-link');
      if (link) link.setAttribute('aria-expanded', 'true');
      this.megaPanel.innerHTML = template.innerHTML;
      this.megaPanel.dataset.owner = item.dataset.megaIndex;
      if (link) this.megaPanel.setAttribute('aria-label', link.textContent.trim());
      this.megaPanel.classList.add('is-open');
      if (focusFirst) this.focusMegaLink(0);
    });
  }

  closeMega() {
    // Same as the 'mega' part of closeAll(), without touching account/drawer/search.
    if (!this.megaPanel) return;
    this.pendingMegaItem = null;
    this.megaPanel.classList.remove('is-open');
    this.megaPanel.innerHTML = '';
    this.megaPanel.removeAttribute('data-owner');
    this.navItems.forEach((item) => {
      item.classList.remove('is-active');
      const link = item.querySelector('.custom-header__nav-link');
      if (link && link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', 'false');
    });
  }

  megaLinks() {
    return Array.from(this.megaPanel.querySelectorAll('a[href]'));
  }

  focusMegaLink(index) {
    const links = this.megaLinks();
    if (!links.length) return;
    const i = Math.max(0, Math.min(index, links.length - 1));
    links[i].focus();
  }

  ownerItem() {
    const owner = this.megaPanel.dataset.owner;
    return owner ? this.querySelector(`[data-nav-item][data-mega-index="${owner}"]`) : null;
  }

  bindMegaMenu() {
    if (!this.navItems.length || !this.megaPanel || !this.middleRow) return;

    this.navItems.forEach((item) => {
      item.addEventListener('mouseenter', () => this.openMega(item));

      const link = item.querySelector('.custom-header__nav-link');
      if (!link) return;
      // Keyboard: focus opens the panel (like hover); ArrowDown moves into it; Escape closes.
      link.addEventListener('focus', () => {
        if (link.matches(':focus-visible')) this.openMega(item);
      });
      link.addEventListener('keydown', (event) => {
        if (!item.hasAttribute('data-has-mega')) return;
        if (event.key === 'ArrowDown' || (event.key === ' ' && !event.repeat)) {
          event.preventDefault();
          this.openMega(item, true);
        } else if (event.key === 'Escape') {
          this.closeAll();
          link.focus();
        }
      });
    });

    this.megaPanel.addEventListener('keydown', (event) => {
      const links = this.megaLinks();
      const i = links.indexOf(document.activeElement);
      const owner = this.ownerItem();
      const ownerLink = owner ? owner.querySelector('.custom-header__nav-link') : null;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeAll();
        if (ownerLink) ownerLink.focus();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.focusMegaLink(i + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (i <= 0 && ownerLink) ownerLink.focus();
        else this.focusMegaLink(i - 1);
      } else if (event.key === 'Tab' && !event.shiftKey && i === links.length - 1 && owner) {
        // Leave the panel to the next top-level link
        const items = Array.from(this.navItems);
        const nextItem = items[items.indexOf(owner) + 1];
        const nextLink = nextItem ? nextItem.querySelector('.custom-header__nav-link') : null;
        if (nextLink) {
          event.preventDefault();
          nextLink.focus();
        }
      } else if (event.key === 'Tab' && event.shiftKey && i === 0 && ownerLink) {
        event.preventDefault();
        ownerLink.focus();
      }
    });

    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) {
        this.closeMega();
      }
    });

    this.middleRow.addEventListener('mouseleave', () => {
      if (window.innerWidth <= 989) return;

      this.closeMegaTimer = window.setTimeout(() => {
        if (!this.matches(':hover')) {
          this.closeAll();
        }
      }, 120);
    });

    this.megaPanel.addEventListener('mouseenter', () => {
      window.clearTimeout(this.closeMegaTimer);
    });

    this.megaPanel.addEventListener('mouseleave', () => {
      this.closeMegaTimer = window.setTimeout(() => {
        this.closeAll();
      }, 120);
    });
  }

bindStickyReveal() {
  if (!this.classList.contains('is-sticky')) return;

  const onScroll = () => {
    const currentY = window.scrollY;
    const delta = currentY - this.lastScrollY;
    const nearTop = currentY <= 10;

    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        if (nearTop) {
          this.classList.remove('is-hidden');
          this.classList.remove('is-sticky-active');
        } else {
          this.classList.add('is-sticky-active');

          if (delta > 4) {
            this.classList.add('is-hidden');
            this.closeAll();
          } else if (delta < -4) {
            this.classList.remove('is-hidden');
          }
        }

        this.lastScrollY = currentY;
        this.ticking = false;
      });

      this.ticking = true;
    }
  };

  this.classList.remove('is-hidden');
  window.addEventListener('scroll', onScroll, { passive: true });
}
}

if (!customElements.get('custom-header')) {
  customElements.define('custom-header', CustomHeader);
}